import { message } from 'antd';
import api from './apiClient';
import {
    archiveLocalScan,
    createLocalScan,
    getLocalDarkReference,
    getLocalResults,
    getLocalScans,
    getLocalSpectra,
    getLocalTestStrips,
    restoreLocalScan,
    saveLocalDarkReference,
    saveLocalResults,
    saveLocalSpectra,
    saveLocalTestStrips,
    updateLocalScan
} from './localScanDb';

// Temporary demo switch. Keep unrelated user/device/library endpoints remote.
export const LOCAL_SCAN_STORAGE = true;

export const fetchAllScanData = async ({ username, dateRange }) => {
    if (LOCAL_SCAN_STORAGE) return getLocalScans({ username, dateRange });
    try {
        let { data } = await api.get(`/fetchAllScanData?username=${username}&from=${dateRange.from}&to=${dateRange.to}`);
        // data = data.map(row => ({ ...row, results: row.results.filter(Boolean).map(substance => ({ substance, predictions: null, is_trace_only: null })) }));
        data = data.map(row => ({
            ...row,
            results: (row.results || []).map(res => ({
                substance: res.substance,
                is_trace_only: res.is_trace_only,
                predictions: null
            }))
        }));
        return data;
    } catch (error) {
        console.error('Fetch all scan data error: ', error);
        return [];
    }
}

export const fetchArchivedScans = async ({ username, dateRange }) => {
    if (LOCAL_SCAN_STORAGE) return getLocalScans({ username, dateRange, archived: true });
    try {
        let { data } = await api.get(`/fetchArchivedScans?username=${username}&from=${dateRange.from}&to=${dateRange.to}`);
        // data = data.map(row => ({ ...row, results: row.results.filter(Boolean).map(substance => ({ substance, predictions: null, is_trace_only: null })) }));
        data = data.map(row => ({
            ...row,
            results: (row.results || []).map(res => ({
                substance: res.substance,
                is_trace_only: res.is_trace_only,
                predictions: null
            }))
        }));
        return data;
    } catch (error) {
        console.error('Fetch all scan data error: ', error);
        return [];
    }
}

export const fetchSpectrum = async (mapID, index = null) => {
    if (LOCAL_SCAN_STORAGE) {
        const spectra = await getLocalSpectra(mapID);
        return index == null ? spectra : spectra.filter(row => row.index === Number(index));
    }
    try {
        const { data } = await api.get('/fetchSpectrum?' + new URLSearchParams({ mapID, index }));
        return data;
    } catch (error) {
        console.error('Spectrum fetch error: ', error);
    }
}

export const fetchArchivedSpectra = async (mapID) => {
    if (LOCAL_SCAN_STORAGE) return getLocalSpectra(mapID);
    try {
        const { data } = await api.get('/fetchArchivedSpectra?' + new URLSearchParams({ mapID }));
        return data;
    } catch (error) {
        console.error('Archived spectrum fetch error: ', error);
    }
}

export const fetchResults = async (mapID) => {
    if (LOCAL_SCAN_STORAGE) return getLocalResults(mapID);
    try {
        const { data } = await api.get('/fetchResults?' + new URLSearchParams({ mapID }));
        return data;
    } catch (error) {
        console.error('Spectrum fetch error: ', error);
    }
}

export const fetchArchivedResults = async (mapID) => {
    if (LOCAL_SCAN_STORAGE) return getLocalResults(mapID);
    try {
        const { data } = await api.get('/fetchArchivedResults?' + new URLSearchParams({ mapID }));
        return data;
    } catch (error) {
        console.error('Spectrum fetch error: ', error);
    }
}

export const uploadResults = (body) => {
    if (LOCAL_SCAN_STORAGE) {
        return saveLocalResults(body.mapID, body.results).then(() => ({ status: 'success', mapID: body.mapID }));
    }
    return new Promise(async (resolve, reject) => {
        api.post('/uploadResults', body)
            .then(({ data }) => {
                if (data.serial || data.status === 'success') {
                    resolve(data);
                } else if (data.status === 'info') {
                    resolve(data);
                } else {
                    reject();
                }
            })
            .catch((error) => {
                console.error('Error:', error);
                reject();
            });
    })
}

export const updateRecord = async (record) => {
    if (LOCAL_SCAN_STORAGE) {
        const updated = await updateLocalScan(record);
        return updated.created_at;
    }
    try {
        const { data } = await api.post('/updateRecord', record);
        return data[0]?.created_at;
    } catch (error) {
        console.error('updateRecord error:', error);
        throw error;
    }
}

export const fetchSubstances = async () => {
    try {
        const { data } = await api.get('/substances');
        return data;
    } catch (error) {
        console.error('Fetch all substances error:', error);
    }
}

export const fetchReferenceSubstanceNames = async () => {
    try {
        const { data } = await api.get('/referenceSubstanceNames');
        return data;
    } catch (error) {
        console.error('Fetch all substances error:', error);
    }
}

export const fetchReferenceSpectrum = async (referenceSubstanceName, integrationTime) => {
    try {
        const { data } = await api.get('/referenceSpectrum?' + new URLSearchParams({ referenceSubstanceName, integrationTime }));
        return data;
    } catch (error) {
        console.error('Reference spectrum fetch error: ', error);
    }
}

export const fetchClientCodes = async (username) => {
    try {
        const { data } = await api.get('/clientcodes?' + new URLSearchParams({ username }));
        return data;
    } catch (error) {
        console.error('Fetch all client codes error:', error);
    }
}

export const createRecord = (body, message, key) => {
    if (LOCAL_SCAN_STORAGE) {
        return createLocalScan(body).then(data => {
            message.success({ content: 'Record added locally.', key, duration: 5 });
            return { status: 'success', serial: data.serial, map_id: data.map_id };
        });
    }
    return new Promise(async (resolve, reject) => {
        api.post('/createRecord', body)
            .then(({ data }) => {
                if (data.serial || data.status === 'success') {
                    message.success({ content: 'Record added.', key, duration: 5 });
                    resolve(data);
                } else {
                    message.error({ content: 'Failed to add record.', key, duration: 5 });
                    reject();
                }
            })
            .catch((error) => {
                console.error('Error:', error);
            });
    })
}

export const uploadSpectra = async (body) => {
    if (LOCAL_SCAN_STORAGE) {
        await saveLocalSpectra(body.map_id, body);
        return { status: 'success' };
    }
    try {
        const { data } = await api.post('/uploadSpectra', body);
        return data;
    } catch (error) {
        console.error('uploadSpectra error:', error);
        throw error;
    }
}

export const saveClientData = async (clientData) => {
    try {
        const compactedPayload = JSON.parse(
            JSON.stringify(clientData, (_key, value) => (value === undefined ? null : value))
        );
        await api.post('/addOrUpdateClientData', compactedPayload);
    } catch (error) {
        console.error('saveClientData error:', error);
        throw error;
    }
}

export const deleteClient = async (username, client_code) => {
    try {
        const { data } = await api.delete('/client', { data: { username, client_code } });
        return data;
    } catch (error) {
        console.error('deleteClient error:', error);
        throw error;
    }
}

export const updateDeviceFirmwareVersionInDB = async (device_id, firmware_version) => {
    try {
        await api.post('/updateDeviceFirmwareVersion', { device_id, firmware_version });
    } catch (error) {
        console.error('updateDeviceFirmwareVersionInDB error:', error);
        throw error;
    }
}

export const updateDeviceNetworkStatusInDB = async (device_id, network_status) => {
    try {
        await api.post('/updateDeviceNetworkStatus', { device_id, network_status });
    } catch (error) {
        console.error('updateDeviceNetworkStatusInDB error:', error);
        throw error;
    }
}

export const updateDeviceCameraStatusInDB = async (device_id, camera_status) => {
    try {
        await api.post('/updateDeviceCameraStatus', { device_id, camera_status });
    } catch (error) {
        console.error('updateDeviceCameraStatusInDB error:', error);
        throw error;
    }
}

export const addDeviceToDatabase = async (body, message, key) => {
    try {
        let { data } = await api.post('/addDevice', body);

        if (data.status === 'success') {
            message.success({ content: 'Device registered!', key, duration: 3 });
            return data;
        }
    } catch (error) {
        message.error({ content: 'Device registration failed.', key, duration: 5 });
        console.error('Error:', error);
    }
}

export const fetchDeviceInfo = async (device_id) => {
    try {
        const { data } = await api.get(`/fetchDeviceInfo?device_id=${device_id || -1}`);
        return data;
    } catch (error) {
        console.error('Device ID not found in databse: ', error);
        return {
            "device_id": device_id,
            "spectrometer_serial": "",
            "wavelength_calibration": [],
            "active_pixels": null,
            "excitation_wavelength": null,
            "bad_pixels": [],
            "intensity_calibration": [],
            "device_serial": "",
            "firmware_version": "",
            "network_status": 0,
            "center_calibration": {}
        }
    }
}

export const fetchDevices = async (deviceIDs) => {
    try {
        const { data } = await api.get(`/fetchDevices?deviceIds=${deviceIDs.toString()}`);
        return data;
    } catch (error) {
        console.error('fetchDevices error: ', error);
    }
}

export const uploadAverageSpectrum = async (averageSpectrumInfo) => {
    try {
        await api.post('/uploadAverageSpectrum', averageSpectrumInfo);
    } catch (error) {
        console.error('uploadAverageSpectrum error: ', error);
    }
}

export const fetchAllMassSpecData = async (massSpecdDateRange) => {
    try {
        const { data } = await api.get('/allMassSpecData?' + new URLSearchParams({ ...massSpecdDateRange }));
        return data;
    } catch (error) {
        console.error('fetchAllMassSpecData error:', error);
        throw error;
    }
}

export const fetchMassSpecDataByUser = async (massSpecdDateRange, username) => {
    try {
        const { data } = await api.get('/massSpecDataByUser?' + new URLSearchParams({ ...massSpecdDateRange, username }));
        return data;
    } catch (error) {
        console.error('fetchMassSpecDataByUser error:', error);
        throw error;
    }
}

export const addMassSpecRow = async (username) => {
    try {
        const { data } = await api.post('/addMassSpecRow', { username });
        return data;

    } catch (error) {
        console.error('addMassSpecRow error:', error);
        throw error;
    }
}

export const deleteMassSpecRow = async (id) => {
    try {
        const { data } = await api.delete('/deleteMassSpecRow', { data: { id } });
        return data;

    } catch (error) {
        console.error('deleteMassSpecRow error:', error);
        throw error;
    }
}

export const saveMassSpecRow = async (massSpecRowData) => {
    try {
        const { data } = await api.post('/updateMassSpecRow', { ...massSpecRowData });
        return data;

    } catch (error) {
        console.error('addMassSpecRow error:', error);
        throw error;
    }
}

export const searchMassSpecData = async (searchString) => {
    try {
        const { data } = await api.post('/searchMassSpecData', { searchString });
        return data;

    } catch (error) {
        console.error('searchMassSpecData error:', error);
        throw error;
    }
}

export const getMassSpecDataById = async (id) => {
    try {
        const { data } = await api.get('/getMassSpecDataById?' + new URLSearchParams({ id }));
        return data;
    }
    catch (error) {
        console.error('getMassSpecDataById error:', error);
        throw error;
    }
}

export const saveSubstanceInfo = async (substanceData) => {
    try {
        await api.post('/updateSubstanceInfo', { ...substanceData });

        message.success({ content: 'Saved!', duration: 3 });
    } catch (error) {
        console.error('saveSubstanceInfo error:', error);
        throw error;
    }
}

export const getExpectedSubstances = async () => {
    try {
        const { data } = await api.get(`/getExpectedSubstances`);
        return data;
    } catch (error) {
        console.error('Error fetching expected substance list: ', error);
        throw error;
    }
}

export const getMapAndLabData = async (dateRange) => {
    try {
        const { data } = await api.get(`/getMapAndLabData?from=${dateRange.from}&to=${dateRange.to}`);
        return data;

    } catch (error) {
        console.error('getMapAndLabData error:', error);
        throw error;
    }
}

export const fetchOperators = async (username) => {
    try {
        const { data } = await api.get('/operators?' + new URLSearchParams({ username }));
        return data;
    } catch (ex) {
        const message = 'Failed to fetch operators.'
        console.error(message, ex);
        return { message, success: false };
    }
}

export const addOperator = async (operatorData) => {
    try {
        const compactedPayload = JSON.parse(
            JSON.stringify(operatorData, (_key, value) => (value === undefined ? null : value))
        );
        const { data } = await api.post('/addOperator', compactedPayload);
        return data;
    } catch (ex) {
        const message = 'Failed to add operator.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const updateOperator = async (operatorData) => {
    try {
        const compactedPayload = JSON.parse(
            JSON.stringify(operatorData, (_key, value) => (value === undefined ? null : value)),
        );

        const { data } = await api.post('/updateOperator', compactedPayload);
        return data;
    } catch (ex) {
        const message = 'Update failed.';
        console.error(message, ex);
        return { success: false, message };
    }
};

export const deleteOperator = async (operatorData) => {
    try {
        const compactedPayload = JSON.parse(
            JSON.stringify(operatorData, (_key, value) => (value === undefined ? null : value)),
        );
        const { data } = await api.delete('/deleteOperator', { data: compactedPayload });
        return data;
    } catch (ex) {
        const message = 'Failed to delete operator profile.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const archiveScan = async (mapID) => {
    if (LOCAL_SCAN_STORAGE) return archiveLocalScan(mapID);
    try {
        const { data } = await api.post('/archiveScan', { mapID });
        return data;
    } catch (ex) {
        const message = 'Failed to archive scan.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const restoreScan = async (mapID) => {
    if (LOCAL_SCAN_STORAGE) return restoreLocalScan(mapID);
    try {
        const { data } = await api.post('/restoreScan', { mapID });
        return data;
    } catch (ex) {
        const message = 'Failed to restore scan.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const uploadTemperatureData = async (temperatureData) => {
    try {
        const compactedPayload = JSON.parse(
            JSON.stringify(temperatureData, (key, value) => (value === undefined ? null : value)),
        );
        const { data } = await api.post('/uploadTemperatureData', compactedPayload);
        return data;
    } catch (ex) {
        const message = 'Failed to upload temperature data.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const getTemperatureData = async (mapID) => {
    try {
        const { data } = await api.get('/temperatureData?' + new URLSearchParams({ mapID }));
        return data;
    } catch (ex) {
        const message = 'Failed to fetch temperature data.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const getArchivedTemperatureData = async (mapID) => {
    try {
        const { data } = await api.get('/archivedTemperatureData?' + new URLSearchParams({ mapID }));
        return data;
    } catch (ex) {
        const message = 'Failed to fetch temperature data.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const getUserGroups = async (username) => {
    try {
        const { data } = await api.get('/userGroups?' + new URLSearchParams({ username }));
        return data;
    } catch (ex) {
        const message = 'Failed to fetch user groups data.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const getUserTraceDetectionPreferences = async (username) => {
    try {
        const { data } = await api.get('/userTraceDetectionPreferences?' + new URLSearchParams({ username }));
        return data;
    } catch (ex) {
        const message = 'Failed to fetch user trace detection preferences data.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const getUserPinCode = async (username) => {
    try {
        const { data } = await api.get('/userPinCode?' + new URLSearchParams({ username }));
        return data;
    } catch (ex) {
        console.error('Failed to fetch user pin code.', ex);
        return null;
    }
}

export const updateUserTraceDetectionPreferences = async (username, preferences) => {
    try {
        const { data } = await api.post('/updateUserTraceDetectionPreferences', { username, preferences });
        return data;
    } catch (ex) {
        const message = 'Failed to update user trace detection preferences data.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const saveDarkReferenceSpectrum = async ({ map_id, darkReferenceSpectrum }) => {
    if (LOCAL_SCAN_STORAGE) {
        await saveLocalDarkReference(map_id, darkReferenceSpectrum);
        return true;
    }
    try {
        const { data } = await api.post('/uploadDarkReferenceSpectrum', { map_id, darkReferenceSpectrum });
        return data.status === 'success';
    } catch (error) {
        console.error('Failed to upload dark reference spectrum', error);
        return false;
    }
}

export const fetchDarkReferenceSpectrum = async (map_id) => {
    if (LOCAL_SCAN_STORAGE) return getLocalDarkReference(map_id);
    try {
        const { data } = await api.get('/darkReferenceSpectrum?' + new URLSearchParams({ map_id }));
        return data.data || null;
    } catch (ex) {
        const message = 'Failed to fetch dark reference spectrum.';
        console.error(message, ex);
        return { success: false, message };
    }
}

export const addSpectrumToValidatedSpectra = async (spectrumData) => {
    if (LOCAL_SCAN_STORAGE) {
        throw new Error('Spectrum validation is unavailable in the local demo.');
    }
    try {
        const { data } = await api.post('/uploadValidatedSpectrum', spectrumData);

        return data.status === 'success';
    } catch (err) {
        if (err.response) {
            const { data: errorData, status, statusText } = err.response;
            if (status === 409) {
                throw new Error(errorData?.error || 'Spectrum already exists in validated spectra.');
            } else {
                throw new Error(errorData?.error || `Unexpected error (${status}): ${statusText}`);
            }
        } else {
            throw new Error(err.message || 'Failed to add spectrum to validated spectra.');
        }
    }
}

export const fetchTestStripResults = async (mapID) => {
    if (LOCAL_SCAN_STORAGE) return getLocalTestStrips(mapID);
    try {
        const { data } = await api.get('/testStripResults?' + new URLSearchParams({ mapID }));
        return data;
    } catch (error) {
        console.error('fetchTestStripResults error:', error);
        return [];
    }
}

export const fetchArchivedTestStripResults = async (mapID) => {
    if (LOCAL_SCAN_STORAGE) return getLocalTestStrips(mapID);
    try {
        const { data } = await api.get('/archivedTestStripResults?' + new URLSearchParams({ mapID }));
        return data;
    } catch (error) {
        console.error('fetchArchivedTestStripResults error:', error);
        return [];
    }
}

export const saveTestStripResults = async (mapID, testStrips) => {
    if (LOCAL_SCAN_STORAGE) {
        await saveLocalTestStrips(mapID, testStrips);
        return { status: 'success' };
    }
    try {
        const { data } = await api.post('/testStripResults', { mapID, testStrips });
        return data;
    } catch (error) {
        console.error('saveTestStripResults error:', error);
        throw error;
    }
}

export const copyMap = async (payload) => {
    try {
        return (await api.post('/copyMap', payload)).data;
    } catch (err) {
        throw new Error(err.message || 'Failed to copy map.');
    }
}
